import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import { MdLanguage } from "react-icons/md";

import { useAuth } from "contexts/AuthContext";
import { MenuItem as MenuItemType } from "types";
import { User as UserType } from "types/firebase";
import { setFilters, resetFilters } from "redux/actions/catalog";
import arrowDown from "assets/icons/arrow-down.svg";
import defaultProfilePicture from "assets/images/Default-Profile-Picture.png";
import { setErrors } from "redux/actions/errors";
import { toggleRecommending } from "redux/actions/admin";
import { RootState } from "redux/reducers";

type MenuItemProps = {
  menu: MenuItemType;
  isDropdown?: boolean;
  user?: UserType;
};

type ContainerProps = {
  readonly toggleDropdown?: boolean;
  readonly accountItem?: boolean;
};

const MainMenuItem: React.FC<MenuItemProps> = ({ menu, isDropdown }) => {
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { recommending } = useSelector((state: RootState) => state.admin);
  const [toggleDropdown, setToggleDropdown] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);

  // Function from CatalogMenuItem.tsx
  const handleClick = async (e: any, key: string) => {
    dispatch(resetFilters());
    if (key === "sign-out") {
      if (recommending) dispatch(toggleRecommending());

      try {
        await logout();
      } catch {
        return dispatch(setErrors({ message: "Error while logging out" }));
      }

      navigate("/sign-out");
    } else if (key === "my-subscriptions") {
      dispatch(setFilters("subscribed", true));
    } else if (key === "my-favorites") {
      dispatch(setFilters("category", e.target.text));
    }
  };

  const handleAccess = () => {
    return (
      isDropdown &&
      currentUser &&
      menu.childAccess &&
      menu.childAccess.includes(currentUser.role)
    );
  };

  const renderMenuItems = (menu: MenuItemType["childMenu"], user: UserType) => {
    if (!menu) return null;

    return menu.map((child) =>
      child.access.includes(user.role) ? (
        <li key={child.key}>
          <DropdownItem
            to={child.path}
            onClick={(e) => handleClick(e, child.key)}
          >
            {child.name}
          </DropdownItem>
        </li>
      ) : null
    );
  };

  useEffect(() => {
    if (location.pathname.split('/')[1].includes(menu.key)) {
      setActive(true);
		} else if (location.pathname.includes('catalog') && menu.key === 'report') {
      setActive(true);
    } else {
			setActive(false);
		}
  }, [location.pathname, menu.name]);

  if (menu.external) {
    return (
      <Container>
        <ExtItemLink href={menu.path} target="_blank" rel="noopener noreferrer">
          {menu.name}
        </ExtItemLink>
      </Container>
    );
  } else if (menu.name === "Translate") {
    return (
      <TranslateDiv id="translate">
        <Globe aria-hidden="true">
          <MdLanguage />
        </Globe>
        <div id="google_translate_element"></div>
      </TranslateDiv>
    );
  } else {
    return (
      <Container
        toggleDropdown={toggleDropdown}
        onMouseOver={() => setToggleDropdown(true)}
        onMouseLeave={() => setToggleDropdown(false)}
        accountItem={menu.key === "signed-in-user"}
      >
        <ItemLink to={menu.path} className="main-link" $underline={active}>
          {menu.key === "signed-in-user" && (
            <ProfilePicture
              src={defaultProfilePicture}
              alt="profile portrait"
            />
          )}
          {menu.key === "signed-in-user" ? currentUser.name : menu.name}{" "}
          {handleAccess() && <DownArrow src={arrowDown} alt="down arrow" />}
        </ItemLink>
        {handleAccess() && (
          <Dropdown toggleDropdown={toggleDropdown}>
            {renderMenuItems(menu.childMenu, currentUser)}
          </Dropdown>
        )}
      </Container>
    );
  }
};

const accountItemStyles = css`
  cursor: pointer;
  padding: 1rem 0;

  a.main-link {
    border-left: 1px solid #fff;
    margin-left: 1.5rem;
    padding-left: 2rem;
  }

  ul {
    right: 0;
  }

  @media screen and (max-width: ${(props) => props.theme.bp.xl}px) {
    a.main-link {
      padding-left: 1rem;
    }
  }
`;

const Container = styled.li<ContainerProps>`
  background-color: ${(props) =>
    props.toggleDropdown ? props.theme.colors.idataDarkBlue : "transparent"};
  font-size: 1.75rem;
  white-space: nowrap;
  position: relative;

  &:not(:last-child) {
    padding: 0 0.5rem;
  }

  ${(props) => (props.accountItem ? accountItemStyles : "")};

  @media screen and (max-width: ${(props) => props.theme.bp.xl}px) {
    font-size: 1.6rem;
  }
`;

const ItemLink = styled(Link)<{ $underline: boolean }>`
  color: #fff;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 5px;

  &:hover {
    background-color: ${(props) => props.theme.colors.idataDarkBlue};
    color: white;

    ${({ $underline }) =>
      $underline &&
      `
    color: #5DC097;
  `}
  }

  ${({ $underline }) =>
    $underline &&
    `
    color: #5DC097;
  `}
`;

const ExtItemLink = styled.a`
  color: #fff;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 5px;

  &:hover {
    background-color: ${(props) => props.theme.colors.idataDarkBlue};
    color: white;
  }
`;

const DownArrow = styled.img`
  margin: auto;
  width: 2rem;
`;

const ProfilePicture = styled.img`
  width: 3.75rem;
  margin-right: 1rem;
`;

const Dropdown = styled.ul<ContainerProps>`
  background-color: ${(props) => props.theme.colors.idataDarkBlue};
  display: ${(props) => (props.toggleDropdown ? "block" : "none")};
  padding: 0.5rem 0 1rem;
  position: absolute;
  top: 50px;
  width: fit-content;
`;

const DropdownItem = styled(Link)`
  color: #fff;
  display: block;
  padding: 1rem 2rem;

  &:hover {
    color: #fff;
    text-decoration: underline;
  }
`;

const TranslateDiv = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 2rem;

  /* Each page has its own header, so this is empty again until Google's script
     reloads. Holding the select's width stops the search bar from filling the gap. */
  #google_translate_element {
    position: relative;
    min-width: 140px;
    min-height: 38px;
  }

  /* Until Google's script has added the languages, show the same pill with
     "English", so the text doesn't vanish on every page load. Browsers without
     :has() skip this rule and show the empty box, as before. */
  #google_translate_element:not(:has(.goog-te-combo option))::before {
    content: "English";
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    width: 140px;
    height: 38px;
    padding: 0 40px 0 42px;
    background-color: rgba(255, 255, 255, 0.14);
    background-image: url(${arrowDown});
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 20px;
    border: 1px solid rgba(255, 255, 255, 0.24);
    border-radius: 999px;
    color: #fff;
    font-size: 15.12px;
    pointer-events: none;
  }

  #google_translate_element > div,
  #google_translate_element .goog-te-combo {
    display: none;
  }
  #google_translate_element > div:first-of-type,
  #google_translate_element .goog-te-combo:first-of-type {
    display: block;
  }

  /* Collapse Google's hidden "Powered by" text so only the select takes space.
     Google sets Arial here, so the select would inherit it instead of the site font */
  #google_translate_element .goog-te-gadget {
    height: auto;
    font-family: inherit;
    font-size: 0;
  }

  /* Fixed width: a native select is as wide as its longest option */
  #google_translate_element .goog-te-combo {
    appearance: none;
    -webkit-appearance: none;
    width: 140px;
    height: 38px;
    margin: 0;
    padding: 0 40px 0 42px;
    background-color: rgba(255, 255, 255, 0.14);
    background-image: url(${arrowDown});
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 20px;
    border: 1px solid rgba(255, 255, 255, 0.24);
    border-radius: 999px;
    color: #fff;
    font-family: inherit;
    font-size: 15.12px;
    transition: background-color 0.2s, border-color 0.2s;

    &:hover {
      background-color: rgba(255, 255, 255, 0.18);
    }

    &:focus {
      outline: none;
      border-color: rgba(255, 255, 255, 0.65);
    }

    option {
      color: #000;
      background-color: #fff;
    }
  }
`;

const Globe = styled.span`
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  color: #fff;
  font-size: 20px;
  pointer-events: none;
  /* Above the "English" placeholder, which is positioned too */
  z-index: 1;
`;
export default MainMenuItem;
